import React from 'react'
import { getVersion, subscribe } from '../lib/portalDb'
import { PortalApiError } from '../lib/portalHttp'

export interface QueryState<T> {
  data: T | undefined
  error: PortalApiError | null
  /** True only on the first load. Refetches after a write keep the current data on screen. */
  loading: boolean
}

/**
 * Runs a portal API read and reruns it after any write, so every screen stays in
 * step with the store without a manual refresh.
 */
export function usePortalQuery<T>(fetcher: () => Promise<T>, deps: React.DependencyList): QueryState<T> {
  const version = React.useSyncExternalStore(subscribe, getVersion, getVersion)
  const [state, setState] = React.useState<QueryState<T>>({ data: undefined, error: null, loading: true })

  React.useEffect(() => {
    let active = true
    setState((current) => ({ ...current, loading: current.data === undefined }))
    fetcher()
      .then((data) => active && setState({ data, error: null, loading: false }))
      .catch((error: unknown) =>
        active &&
        setState({
          data: undefined,
          error: error instanceof PortalApiError ? error : new PortalApiError('INTERNAL_ERROR'),
          loading: false,
        }),
      )
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, ...deps])

  return state
}

/** Wraps a portal write with pending and error state for a button or form. */
export function usePortalMutation<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = React.useState(false)
  const [error, setError] = React.useState<PortalApiError | null>(null)

  const run = React.useCallback(
    async (...args: A): Promise<R | undefined> => {
      setPending(true)
      setError(null)
      try {
        return await fn(...args)
      } catch (caught) {
        setError(caught instanceof PortalApiError ? caught : new PortalApiError('INTERNAL_ERROR'))
        return undefined
      } finally {
        setPending(false)
      }
    },
    [fn],
  )

  return { run, pending, error, reset: () => setError(null) }
}
